import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import {
  PADELUTION_FORMATS,
  buildPadelutionPayload,
  getPadelutionFormat,
  playerCountHint,
  validatePlayerCount,
} from '../utils/padelutionFormats'

/**
 * Assembles a Padelution tournament from an activity's participants.
 *
 * EXPERIMENTAL: nothing is sent anywhere. The backend endpoint does not exist yet,
 * so pressing Luo shows the payload that would be sent and says so. Creating the
 * tournament for real has to happen server side -- the padelution-tournament client
 * is a headless Node scraper that logs in as a Padelution user, so it can neither
 * run in the browser nor hold its credentials there.
 */
function PadelutionTournamentDialog({ open, activity, onClose }) {
  const participants = React.useMemo(() => (activity && activity.participants) || [], [activity])

  const [slug, setSlug] = React.useState('americano')
  const [name, setName] = React.useState('')
  const [result, setResult] = React.useState(null)

  React.useEffect(() => {
    if (open) {
      setSlug('americano')
      setName(activity && activity.name && activity.name.fi ? activity.name.fi : '')
      setResult(null)
    }
  }, [open, activity])

  const format = getPadelutionFormat(slug)
  const built = buildPadelutionPayload({ slug, name, participants })
  const validity = validatePlayerCount(slug, built.playerCount)

  const handleCreate = () => setResult(built)

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Luo turnaus Padelutioniin</DialogTitle>
      <DialogContent>
        <TextField
          label='Turnauksen nimi'
          variant='standard'
          margin='normal'
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          select
          label='Turnaustyyppi'
          variant='standard'
          margin='normal'
          fullWidth
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value)
            setResult(null)
          }}
        >
          {PADELUTION_FORMATS.map((option) => (
            <MenuItem key={option.slug} value={option.slug}>
              {option.label} ({playerCountHint(option.slug)})
            </MenuItem>
          ))}
        </TextField>

        {format ? (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {format.description}
          </Typography>
        ) : null}

        <Typography variant='body2' sx={{ mt: 2 }}>
          Osallistujia: <b>{built.playerCount}</b>
        </Typography>
        {built.deduped > 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {built.deduped === 1
              ? 'Yhdellä pelaajalla oli sama nimi kuin toisella, joten nimeen lisättiin numero. Padelution vaatii uniikit nimet.'
              : built.deduped +
                ' pelaajalla oli sama nimi kuin toisella, joten nimiin lisättiin numero. Padelution vaatii uniikit nimet.'}
          </Typography>
        ) : null}
        {built.skipped > 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {built.skipped} osallistujaa ohitettiin, koska nimi puuttuu.
          </Typography>
        ) : null}

        {!validity.ok ? (
          <Alert severity='warning' sx={{ mt: 2 }}>
            {validity.reason}
          </Alert>
        ) : null}

        {result ? (
          <>
            <Alert severity='info' sx={{ mt: 2 }}>
              Padelution-integraatio ei ole vielä käytössä. Alla on tiedot, jotka turnauksesta
              lähetettäisiin. Mitään ei tallennettu eikä lähetetty.
            </Alert>
            <Box
              component='pre'
              sx={{
                mt: 1,
                p: 1,
                fontSize: 12,
                backgroundColor: '#f4f3ef',
                borderRadius: 1,
                maxHeight: 260,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(result.payload, null, 2)}
            </Box>
            <Typography variant='caption' color='text.secondary'>
              Huom: Padelution ei ota vastaan aloitusaikaa, kestoa eikä kenttiä, joten aktiviteetin
              ajankohta ei siirry turnaukseen.
            </Typography>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sulje</Button>
        <Button onClick={handleCreate} variant='contained' color='primary' disabled={built.playerCount === 0}>
          Luo
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PadelutionTournamentDialog
